import { debounce, get } from 'radash';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useDataModel } from '@/hooks/useDataModel';
import { useExcludedFromSaveFields } from '@/hooks/useExcludedFromSaveFields';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { RealTimeMessagingActions, RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { useAttributeFieldPathParser } from '../../../components/datamodel/hooks/useAttributeFieldPathParser';

/**
 * Listens for item changes by other users and share item changes to any
 * other user who is editing the same item.
 */
const useRealTimeItemChangesListener = ({ primaryKey }: { primaryKey?: string }) => {

    const { t } = useTranslation();
    const { dataModel } = useDataModel();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { toast } = useToast();
    const { user } = useUser();

    const { getAttributeNameFromFormPath } = useAttributeFieldPathParser();
    const { setValue, watch } = useFormContext();
    const { addExcludedField } = useExcludedFromSaveFields();

    // Channel registration is owned by useItemEditedChannel in ItemEditorDialog.
    // This hook only listens for ITEM_EDITED payloads and broadcasts local edits.
    const { broadcast } = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.ITEM_EDITED_EVENT,
        identifier: primaryKey,
        eventHandler: (data) => {

            if (data.id === user?.id) {
                // Ignore the event if it's originating from us
                return;
            }

            if (data.action === RealTimeMessagingActions.ITEM_EDITED) {

                logger.debug('RealTimeItemChangesListener: Received item edited event', data);

                const updatedAttributesPaths = Object.keys(data.item);
                updatedAttributesPaths.forEach(updatedAttributePath => {

                    // Get the attribute name from the path, in case of complex paths, the last attribute is the one we are interested in.
                    const attributeName = getAttributeNameFromFormPath(updatedAttributePath);
                    if (!attributeName) {
                        logger.error(`RealTimeItemChangesListener: Trying to update item with attribute path ${updatedAttributePath} which we couldn't parse`);
                        return;
                    }

                    const attribute = dataModel?.attribute(attributeName);
                    if (!attribute) {
                        logger.error(`RealTimeItemChangesListener: Trying to update item with attribute ${attributeName} but it was not found`);
                        return;
                    }

                    // Prepare toast message with the attribute name and the user name
                    // FIXME: Uncouple from the AngularJS implementation
                    const $angularJsInjector = getInjector();
                    const $rootScope = getRootScope($angularJsInjector);
                    const message = t('USER_UPDATED_FIELD', {
                        user: $rootScope.getUserName(user?.id),
                        field: translateAttributeLabel(attribute),
                    });
                    toast.info(message);

                    setValue(updatedAttributePath, data.item[updatedAttributePath], {
                        shouldDirty: false,
                    });

                    // Track this field as excluded from save
                    addExcludedField(updatedAttributePath);
                });
            }
        }
    });

    useEffect(() => {

        if (!primaryKey) {
            // Nothing to broadcast as this is a new item.
            return;
        }

        const onItemChange = debounce({ delay: 500 }, (item, { name: attributeName, type }) => {

            // Ignore changes that are set by setValues (as there is no type), otherwise
            // we would have a ping-pong situation if we listen to changes that are transmitted from another user.
            if (!type) {
                return;
            }

            logger.debug('RealTimeItemChangesListener: Item changes will be broadcasted to users working on the same item', item, attributeName, type);

            broadcast({
                action: RealTimeMessagingActions.ITEM_EDITED,
                item: {
                    // Ensure that we don't transmit undefined, because it won't be transmitted at all!
                    // instead, transmit null to indicate that the attribute has been cleared and let the receiving
                    // side set the appropriate empty/false/whatever value.
                    [attributeName]: get(item, attributeName) || null,
                },
                id: user?.id,
                userId: user?.userId,
            });

        });

        const itemChangesSubscription = watch(onItemChange);

        return () => itemChangesSubscription.unsubscribe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primaryKey]);

    return null;
};

export { useRealTimeItemChangesListener };
